import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { Offer as OfferGraphql, Package, SportObjectType } from '@/@types/graphql-types';
import type { ClickoutPlacement, ClickoutType } from '@/interfaces/snowplow/clickout-context';

import { MonetizationType, ObjectType, OfferType, PresentationType } from '@/@types/graphql-types';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyboxOfferCategory } from '@/interfaces/buybox';

import { useI18n } from '@/helpers/composables/useI18n';

const CATEGORY_MONETIZATIONS_GRAPHQL: Record<BuyboxOfferCategory, MonetizationType[] | 'BUNDLE'> = {
	[BuyboxOfferCategory.STREAM]: [
		MonetizationType.Flatrate,
		MonetizationType.Free,
		MonetizationType.Ads,
		MonetizationType.FlatrateAndBuy,
	],
	[BuyboxOfferCategory.RENT]: [MonetizationType.Rent],
	[BuyboxOfferCategory.BUY]: [MonetizationType.Buy],
	[BuyboxOfferCategory.PHYSICAL]: [MonetizationType.Buy],
	[BuyboxOfferCategory.CINEMA]: 'BUNDLE',
	// We need add BUNDLE as Monetization Type (exception) to be able to display bundles in Promoted BuyBox
	[BuyboxOfferCategory.BUNDLE]: 'BUNDLE',
};

export const MONETIZATION_TYPE_TRANSLATION_KEY: Partial<Record<MonetizationType, string>> = {
	ADS: 'WEBAPP_FREE_WITH_ADS',
	BUY: 'WEBAPP_BUY',
	CINEMA: 'WEBAPP_CINEMA',
	FAST: 'WEBAPP_FREE_TV',
	FLATRATE: 'WEBAPP_SUBSCRIPTION',
	FLATRATE_AND_BUY: 'WEBAPP_BUY',
	FREE: 'WEBAPP_FREE',
	RENT: 'WEBAPP_RENT',
	LINEAR_FLATRATE: 'WEBAPP_SUBSCRIPTION',
	LINEAR_FREE: 'WEBAPP_FREE',
};

export const DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY: Record<
	PresentationType.Dvd | PresentationType.Bluray | PresentationType.Bluray_4K,
	Partial<Record<MonetizationType, string>>
> = {
	[PresentationType.Dvd]: {
		[MonetizationType.Buy]: 'WEBAPP_BUY_DVD',
		[MonetizationType.Rent]: 'WEBAPP_RENT_DVD',
	},
	[PresentationType.Bluray]: {
		[MonetizationType.Buy]: 'WEBAPP_BUY_BLURAY',
		[MonetizationType.Rent]: 'WEBAPP_RENT_BLURAY',
	},
	[PresentationType.Bluray_4K]: {
		[MonetizationType.Buy]: 'WEBAPP_BUY_BLURAY',
		[MonetizationType.Rent]: 'WEBAPP_RENT_BLURAY',
	},
};

export type NarrativeObjectType =
	| ObjectType.Show
	| ObjectType.ShowEpisode
	| ObjectType.ShowSeason
	| ObjectType.Movie
	| ObjectType.Person;

export const enum OfferLocation {
	REGULAR_BUYBOX = 0,
	PROMOTED_BUYBOX = 1,
	BACKDROP = 2,
	MIDDLE_BUYBOX = 3,
	FREE_TRIAL = 4,
	BANNER_BUYBOX = 5,
	EPISODE_BUYBOX = 6,
}

// These strings are checked as is in JW-UCT becareful if changing them
export type BuyboxParam = (typeof BuyboxParam)[keyof typeof BuyboxParam];
export const BuyboxParam = {
	Normal: 'normal',
	Promoted: 'special',
	Sport: 'sports',
} as const;

export function getBuyboxCategoryByMonetizationGraphql(
	monetizationType: MonetizationType
): BuyboxOfferCategory | undefined {
	return (Object.keys(CATEGORY_MONETIZATIONS_GRAPHQL) as (keyof typeof CATEGORY_MONETIZATIONS_GRAPHQL)[]).find(
		key => CATEGORY_MONETIZATIONS_GRAPHQL[key].indexOf(monetizationType) !== -1
	);
}

export function getTranslatableForShowOfferGraphql(offer: TitleOfferFragment, objectType: ObjectType) {
	if (offer.elementCount == null || offer.elementCount === 0) {
		return null;
	}

	const { t, tc } = useI18n();

	if (objectType === ObjectType.ShowSeason) {
		return offer.elementCount > 1
			? t('WEBAPP_X_EPISODES', { episodes: offer.elementCount })
			: tc('WEBAPP_ONE_EPISODE');
	}

	if (objectType === ObjectType.Show) {
		return offer.elementCount > 1
			? t('WEBAPP_X_SEASONS', { seasons: offer.elementCount })
			: tc('WEBAPP_ONE_SEASON');
	}
}

export function getTranslatableForOfferGraphql(offer: TitleOfferFragment, objectType: ObjectType) {
	const translatableForShowOfferGraphql = getTranslatableForShowOfferGraphql(offer, objectType);

	if ((offer.type === OfferType.Aggregated || !offer.retailPriceValue) && translatableForShowOfferGraphql) {
		return translatableForShowOfferGraphql;
	}

	const { tc } = useI18n();

	return (
		{
			[MonetizationType.Flatrate]: tc('WEBAPP_FLAT'),
			[MonetizationType.FlatrateAndBuy]: tc('WEBAPP_FLAT'),
			[MonetizationType.Ads]: tc('WEBAPP_ADS'),
			[MonetizationType.Free]: tc('WEBAPP_FREE'),
			[MonetizationType.Cinema]: tc('WEBAPP_CINEMA'),
		}[offer.monetizationType as string] ?? false
	);
}

export function trackClickOutSports(
	offer: Pick<
		OfferGraphql,
		'monetizationType' | 'presentationType' | 'retailPriceValue' | 'currency' | 'lastChangeRetailPriceValue'
	> & { package: Pick<Package, 'packageId' | 'clearName' | 'technicalName' | 'shortName'> },
	modulePositionInList: number | undefined,
	matchPositionInList: number | undefined,
	sportObjectType: SportObjectType,
	offerLocation: string,
	additionalContexts?: SnowplowContext[],
	placement?: ClickoutPlacement,
	clickoutType?: ClickoutType
) {
	// A bit hacky but for clickouts, we do a double tracking.
	// We track in Snowplow (slow) and directly in the consumer api (real time).
	const contexts: SnowplowContext[] = [
		// clickout context
		SnowplowClickoutContextGraphql.fromProviderOffer(
			offer,
			sportObjectType,
			undefined,
			undefined,
			placement,
			clickoutType
		),
		...(additionalContexts ? additionalContexts : []),
		// package context
		TrackingHelper.getPackageContext(offer.package.packageId, offer.package.technicalName),
	];

	TrackingHelper.trackEvent(
		'clickout',
		{
			action: 'sports',
			label: offerLocation,
			property:
				modulePositionInList !== undefined && matchPositionInList !== undefined
					? `${modulePositionInList}_${matchPositionInList}`
					: undefined, // <{position of module in list}__{position of match in list}>
		},
		contexts
	);

	if (offerLocation === 'watchnow') {
		// legacy reasons we need to additionally track watchnow events
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'watchnow_clicked',
				label: 'sports',
			},
			contexts
		);
	}
}
